import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { errorToastId } from '@/shared/request-feedback';
import {
  describeOfferRegisterError,
  isUuid,
  readApiErrorCode,
} from './offer-errors';
import { offersApiDatasource } from './offers-api.datasource';
import type { GatewayOfferIds } from './offers-api.datasource';

const OFFERS_KEY = ['sales', 'offers'];
/** As duas chamadas ao provedor, para o modal saber que há uma em voo. */
export const OFFER_PROVIDER_CALL_KEY = ['sales', 'offers', 'provider-call'];

/** Só busca com o UUID completo — o back recusaria o resto com 400. */
export function useOfferSearch(term: string) {
  const externalId = term.trim();
  const enabled = isUuid(externalId);
  const query = useQuery({
    queryKey: [...OFFERS_KEY, externalId],
    queryFn: () => offersApiDatasource.findByExternalId(externalId),
    enabled,
    staleTime: 1000 * 60,
  });
  return {
    options: query.data ?? [],
    isLoading: enabled && query.isFetching,
    isIncomplete: externalId.length > 0 && !enabled,
    isEmpty: enabled && query.isSuccess && (query.data?.length ?? 0) === 0,
    isError: enabled && query.isError,
  };
}

export function useOfferProviderAccounts() {
  return useQuery({
    queryKey: ['sales', 'provider-accounts', 'offer-search'],
    queryFn: () => offersApiDatasource.listProviderAccounts(),
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * O toast global mostraria a mensagem crua do Guru; o da tela substitui o dele
 * pelo mesmo id (ver `request-feedback.ts`).
 */
function friendlyToast(err: unknown) {
  toast.error(describeOfferRegisterError(err).message, {
    id: errorToastId(err),
  });
}

export function useLookupGatewayOffer() {
  return useMutation({
    mutationKey: OFFER_PROVIDER_CALL_KEY,
    mutationFn: (ids: GatewayOfferIds) => offersApiDatasource.lookup(ids),
    onError: friendlyToast,
  });
}

/**
 * Alguém cadastrou entre a conferência e a confirmação: o 409 vira sucesso com a
 * oferta que já existe, e nem o toast global de falha aparece.
 */
async function registerOrFindExisting(ids: GatewayOfferIds) {
  try {
    return await offersApiDatasource.register(ids);
  } catch (err) {
    if (readApiErrorCode(err) !== 'OFFER_ALREADY_EXISTS') throw err;
    const found = await offersApiDatasource
      .findByExternalId(ids.externalOfferId)
      .catch(() => []);
    const existing = found.find(
      (o) => o.providerAccountId === ids.providerAccountId,
    );
    if (!existing) throw err;
    return existing;
  }
}

export function useRegisterOffer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: OFFER_PROVIDER_CALL_KEY,
    mutationFn: registerOrFindExisting,
    onError: friendlyToast,
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: OFFERS_KEY }),
  });
}
